import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { snakeCase } from 'lodash';

import { AllocationApi, Configuration } from '../_openapi/portfolio-optimizer';
import { processFilter } from '../widgets/filters/filterUtils';
import { defaultApiConfig } from './defaultApiConfig';

const allocationApi = new AllocationApi(new Configuration(defaultApiConfig));

export type AllocationVariant = 'growth' | 'secured' | 'cash';

interface QueryParams {
    pagination?: PaginationState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
    enabled?: boolean;
}

const buildQueryParams = (params: QueryParams) => {
    const query: Record<string, unknown> = {};
    if (params.pagination) {
        query.pageIndex = params.pagination.pageIndex;
        query.pageSize = params.pagination.pageSize;
    }

    if (params.sorting?.length) {
        query.sortLabel = snakeCase(params.sorting[0].id);
        query.sortDesc = params.sorting[0].desc;
    }

    if (params.columnFilters?.length) {
        query.filter = params.columnFilters.map(processFilter);
    }

    return query;
};

export const usePreTradeAllocationReport = (params: QueryParams) =>
    useQuery({
        queryKey: ['preTradeAllocationReport', 'list', params],
        queryFn: () => allocationApi.getPreTradeGrowthAllocation(buildQueryParams(params)),
        placeholderData: keepPreviousData,
        enabled: params.enabled ?? true,
    });

export const useSecuredAllocationReport = (params: QueryParams) =>
    useQuery({
        queryKey: ['securedAllocationReport', 'list', params],
        queryFn: () => allocationApi.getSecuredAllocation(buildQueryParams(params)),
        placeholderData: keepPreviousData,
    });

export const useCashAllocationReport = (params: QueryParams) =>
    useQuery({
        queryKey: ['cashAllocationReport', 'list', params],
        queryFn: () => allocationApi.getCashAllocation(buildQueryParams(params)),
        placeholderData: keepPreviousData,
    });

export const useAccountAllocation = (accountGUID?: string) =>
    useQuery({
        queryKey: ['accountAllocation', accountGUID],
        queryFn: () => allocationApi.getAccountAllocation({ accountGuid: accountGUID! }),
        enabled: !!accountGUID,
    });
